import React, { useState, useEffect } from 'react';
import youtubeService from '../services/youtubeService';
import promptService from '../services/promptService';

const YouTubeSubscriptionForm = ({ initialData, isEditing, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    channel_id: initialData?.channel_id || '',
    channel_name: initialData?.channel_name || '',
    subscription_type: initialData?.subscription_type || 'Teams',
    teams_webhook_url: initialData?.teams_webhook_url || '',
    teams_channel_name: initialData?.teams_channel_name || '',
    bedrock_prompt_name: initialData?.bedrock_prompt_name || '',
    notify_all: initialData?.notify_all || false,
  });

  const [teamsChannels, setTeamsChannels] = useState({});
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [prompts, setPrompts] = useState([]);
  const [loadingPrompts, setLoadingPrompts] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTeamsChannels();
    fetchPrompts();
  }, []);

  const fetchTeamsChannels = async () => {
    try {
      setLoadingChannels(true);
      const data = await youtubeService.getTeamsChannels();
      setTeamsChannels(data.channels || {});
    } catch (err) {
      console.error('Error fetching Teams channels:', err);
      setError('Failed to load Teams channels');
    } finally {
      setLoadingChannels(false);
    }
  };

  const fetchPrompts = async () => {
    try {
      setLoadingPrompts(true);
      const response = await promptService.getPrompts();
      // Filter prompts that start with "YT_"
      const ytPrompts = (response.prompts || []).filter(prompt =>
        prompt.name && prompt.name.startsWith('YT_')
      );
      setPrompts(ytPrompts);
    } catch (err) {
      console.error('Error fetching prompts:', err);
      // Don't set error here, just log it - prompts are optional
    } finally {
      setLoadingPrompts(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTeamsChannelChange = (e) => {
    const channelName = e.target.value;
    const webhookUrl = teamsChannels[channelName] || '';
    setFormData(prev => ({
      ...prev,
      teams_channel_name: channelName,
      teams_webhook_url: webhookUrl
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.channel_id.trim()) {
      setError('Channel ID is required');
      return;
    }
    if (!formData.channel_name.trim()) {
      setError('Channel Name is required');
      return;
    }
    if (
      formData.subscription_type === 'Teams' &&
      !formData.teams_webhook_url.trim()
    ) {
      setError('Teams Webhook URL is required');
      return;
    }

    // Validate YouTube channel ID format (should start with UC)
    if (!formData.channel_id.startsWith('UC')) {
      setError('YouTube channel ID should start with "UC"');
      return;
    }

    try {
      setSaving(true);
      await onSave(formData);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save subscription');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      <div className="mb-3">
        <label htmlFor="channel_id" className="form-label">
          YouTube Channel ID <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className="form-control"
          id="channel_id"
          name="channel_id"
          value={formData.channel_id}
          onChange={handleChange}
          placeholder="UCxxxxxxxxxxxxxxxxxxxxxxx"
          disabled={isEditing}
          required
        />
        <small className="text-muted">
          Find this in the channel URL: youtube.com/channel/[CHANNEL_ID]
        </small>
      </div>

      <div className="mb-3">
        <label htmlFor="channel_name" className="form-label">
          Channel Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          className="form-control"
          id="channel_name"
          name="channel_name"
          value={formData.channel_name}
          onChange={handleChange}
          placeholder="e.g., Tech Channel"
          required
        />
        <small className="text-muted">
          A friendly name to identify this channel
        </small>
      </div>

      <div className="mb-3">
        <label htmlFor="subscription_type" className="form-label">
          Subscription Type <span className="text-danger">*</span>
        </label>
        <select
          className="form-select"
          id="subscription_type"
          name="subscription_type"
          value={formData.subscription_type}
          onChange={handleChange}
          required
        >
          <option value="Teams">Teams</option>
          <option value="Research">Research</option>
        </select>
        <small className="text-muted">
          Teams sends Teams notifications + summaries. Research stores
          transcripts for research ingestion.
        </small>
      </div>

      {formData.subscription_type === 'Teams' && (
        <div className="mb-3">
          <label htmlFor="teams_channel_name" className="form-label">
            MS Teams Channel
            {formData.subscription_type === 'Teams' && (
              <span className="text-danger"> *</span>
            )}
          </label>
          <select
            className="form-select"
            id="teams_channel_name"
            name="teams_channel_name"
            value={formData.teams_channel_name}
            onChange={handleTeamsChannelChange}
            required={formData.subscription_type === 'Teams'}
            disabled={loadingChannels}
          >
            <option value="">Select a Teams channel...</option>
            {Object.keys(teamsChannels).map((channelName) => (
              <option key={channelName} value={channelName}>
                {channelName}
              </option>
            ))}
          </select>
          <small className="text-muted">
            {formData.subscription_type === 'Teams'
              ? 'Select the Teams channel where video notifications will be sent'
              : 'Not used for Research subscriptions'}
          </small>
        </div>
      )}

      <div className="mb-3">
        <label htmlFor="bedrock_prompt_name" className="form-label">
          Analysis Prompt Name
        </label>
        <select
          className="form-select"
          id="bedrock_prompt_name"
          name="bedrock_prompt_name"
          value={formData.bedrock_prompt_name}
          onChange={handleChange}
          disabled={loadingPrompts}
        >
          <option value="">Use default prompt</option>
          {prompts.map((prompt) => (
            <option key={prompt.prompt_id} value={prompt.prompt_id}>
              {prompt.name}
            </option>
          ))}
        </select>
        <small className="text-muted">
          Bedrock prompt to use for video transcript analysis. Select a prompt with "YT_" prefix or leave blank to use default.
        </small>
      </div>

      {formData.subscription_type === 'Teams' && (
        <div className="mb-3">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="notify_all"
              name="notify_all"
              checked={formData.notify_all}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor="notify_all">
              Always Notify
            </label>
          </div>
          <small className="text-muted d-block mt-1">
            If checked, send notifications for all videos. If unchecked,
            only send notifications when Bedrock analysis marks the video
            as important.
          </small>
        </div>
      )}

      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            <>
              <i className="bi bi-check-circle me-2"></i>
              {isEditing ? 'Update' : 'Create'} Subscription
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default YouTubeSubscriptionForm;
